import React, { useEffect } from "react";
import { FiArrowLeft, FiSearch, FiShare2, FiFlag } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import genericUtility from "../../../../../../services/utility/GenericUtility";
import "./styles/CompactHeaderV2.css";

interface CompactHeaderV2Props {
  productImage?: string;
  productTitle?: string;
  productPrice?: number;
  displayPrice?: string;
  productId?: string;
  opacity: number;
  showProductInfo?: boolean;
  overlayMode?: boolean;
  isAuthenticated?: boolean;
  onShare?: () => void;
  /** Omit entirely to hide the report icon (e.g. viewing your own listing). */
  onReport?: () => void;
}

const CompactHeaderV2: React.FC<CompactHeaderV2Props> = ({
  productImage,
  productTitle,
  productPrice,
  displayPrice,
  productId,
  opacity,
  showProductInfo = true,
  overlayMode = false,
  isAuthenticated = true,
  onShare,
  onReport,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoBack = () => {
    try {
      if (location.state?.fromSearch) return navigate("/search");
      if (location.state?.fromNineFinds) return navigate("/ninefinds");
      if (location.state?.fromHome) return navigate("/home");
      if (location.state?.fromListings) return navigate("/my-listings");
      if (location.state?.fromMessages) return navigate("/my-messages");
      if (location.state?.fromProfile) return navigate("/my-profile");

      const origin = genericUtility.getOriginFrom();
      if (origin) {
        genericUtility.clearOriginFrom();
        return navigate(origin);
      }

      window.history.length > 1 ? navigate(-1) : navigate("/home");
    } catch {
      navigate("/home");
    }
  };

  // Edge swipe-to-go-back (left edge → swipe right)
  useEffect(() => {
    let startX = 0, startY = 0, startTime = 0;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t.clientX > 20) return;
      const el = e.target as HTMLElement;
      if (el.closest("button,input,[role='slider'],.offer-slider,.slider-track")) return;
      startX = t.clientX; startY = t.clientY; startTime = Date.now();
    };

    const onTouchEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - startX, dy = t.clientY - startY, dt = Date.now() - startTime;
      if (startX <= 20 && dx > 120 && Math.abs(dy) < 100 && dt < 400 && Math.abs(dx) > Math.abs(dy) * 2) {
        e.preventDefault();
        window.history.length > 1 ? window.history.back() : navigate("/home");
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: false });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return (
    <div
      className={`chv2${overlayMode ? " chv2--overlay" : ""}`}
      style={{ opacity, transform: opacity > 0 ? "translateY(0)" : "translateY(-0.5rem)" }}
    >
      <div className={`chv2__content${!showProductInfo ? " chv2__content--buttons-only" : ""}`}>
        <button className="chv2__back" onClick={(e) => { e.stopPropagation(); handleGoBack(); }} aria-label="Go back">
          <FiArrowLeft size={20} />
        </button>

        {showProductInfo && (
          <div
            className="chv2__info"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            {productImage && (
              <img className="chv2__thumb" src={productImage} alt={productTitle ?? "Product"} />
            )}
            <div className="chv2__text">
              <div className="chv2__title" title={productTitle}>{productTitle}</div>
              {displayPrice && (typeof productPrice === "number" && (
                <div className="chv2__price">₹{productPrice}</div>
              ))}
            </div>
          </div>
        )}

        <div className="chv2__actions">
          {isAuthenticated && (
            <button
              className="chv2__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/search", { state: { fromProductDetails: true, productId } });
              }}
              aria-label="Search"
            >
              <FiSearch size={20} />
            </button>
          )}
          <button
            className="chv2__action-btn"
            onClick={(e) => { e.stopPropagation(); onShare?.(); }}
            aria-label="Share"
          >
            <FiShare2 size={20} />
          </button>
          {onReport && (
            <button
              className="chv2__action-btn"
              onClick={(e) => { e.stopPropagation(); onReport(); }}
              aria-label="Report listing"
              title="Report listing"
            >
              <FiFlag size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactHeaderV2;
